<script setup lang="ts">
import type { Query } from '@directus/types'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

if (import.meta.client)
  gsap.registerPlugin(ScrollTrigger)

let gsapContext: ReturnType<typeof gsap.context>

definePageMeta({
  title: 'Industries',
  description: 'ICS-engineered components are used in medical, semiconductor, aerospace, testing, defense, and robotics industries.',
  order: 4,
})

const config = useRuntimeConfig()

const triggerElements = ref<HTMLDivElement[]>([])

const query: Query = {
  filter: {
    status: { _eq: 'published' },
  },
}

const { data: industries } = await useFetch<DefaultIndustry[]>(
  '/api/items/industries',
  {
    key: 'page:industries:all-industries',
    query,
    getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key],
  },
)

function toAnchor(index: number, to?: string) {
  if (!triggerElements.value[index])
    return

  const tweens = gsap.getTweensOf(triggerElements.value[index])

  tweens.forEach((tween) => {
    if (!tween || !tween.scrollTrigger)
      return
    const snap = tween.scrollTrigger.getTween(true)
    if (snap)
      snap.progress(1).kill()
  })
  navigateTo(`#${to}`)
}

onMounted(() => {
  gsapContext = gsap.context(() => {
    triggerElements.value.forEach((panel) => {
      const q = gsap.utils.selector(panel)

      const text = q('.text-el') as HTMLElement[]

      const video = q('video')[0] as HTMLVideoElement

      const overlays = q('.overlay-el') as HTMLDivElement[]

      gsap.set(text, {
        opacity: 0,
      })

      gsap.set(overlays, {
        opacity: 0,
      })

      gsap.set(video, {
        filter: 'brightness(1.2)',
      })

      gsap.to(text, {
        opacity: 1,
        duration: 0.5,
      })

      gsap.to(overlays, {
        opacity: 1,
        scrollTrigger: {
          trigger: panel,
          scrub: true,
          start: 'top 38%',
          end: 'top',
        },
      })

      gsap.to(video, {
        ease: 'none',
        filter: 'brightness(1)',
        scrollTrigger: {
          trigger: panel,
          scrub: true,
          onUpdate: (self) => {
            if (video.ended)
              video.play()
            video.playbackRate = gsap.utils.interpolate(1, 0.5, self.progress)
          },
        },
      })

      gsap.to(panel, {
        scrollTrigger: {
          trigger: panel,
          pin: true,
          pinSpacing: false,
          start: 'top 5%',
          scrub: true,
          snap: {
            snapTo: [0, 1],
            directional: false,
          },
        },
      })
    })
  })
})

onUnmounted(() => {
  if (gsapContext)
    gsapContext.revert()
})
</script>

<template>
  <PageRoot>
    <PageHead
      id="top"
      theme="dark"
    >
      <BlockContent class="mb-10">
        <p class="text-lg">
          ICS-engineered components are ready for these industries and more.
        </p>
      </BlockContent>
      <template #parallax>
        <div class="absolute-inset from-icsblue to-icsblue-600 bg-linear-to-b mix-blend-multiply -z-1" />
        <NuxtImg
          src="bf49ee1e-8195-4da9-8452-a64a55e674b4"
          alt="A decorative background textured like a mountainous rock-face."
          class="absolute-inset object-cover brightness-130 contrast-80 size-full -z-4 dark:brightness-90"
        />
      </template>
    </PageHead>
    <BlockWindow
      v-if="industries"
      element="section"
      class="text-white heading"
    >
      <div
        v-for="industry, index in industries"
        :id="industry.slug"
        ref="triggerElements"
        :key="industry.slug"
        class="panel flex flex-col h-screen w-full shadow-black shadow-lg justify-center relative"
      >
        <NuxtLink :to="`/industries/${industry.slug}`">
          <h2
            class="text-el heading-link-bright text-white heading-strong w-content mb-10 opacity-0 w-full hyphens-manual drop-shadow-md drop-shadow-color-black/38 f-text-36-72 dark:text-white sm:hyphens-none sm:f-pl-0-64"
            v-html="industry.display_name || industry.name"
          />
        </NuxtLink>
        <ul class="text-el w-content list-disc list-outside opacity-0 w-full f-text-18-36 f-pl-30-112 marker:text-icsred">
          <li
            v-for="bullet in industry.examples"
            :key="bullet"
            class="drop-shadow-xs drop-shadow-color-black"
            v-html="bullet"
          />
          <!-- eslint-disable-next-line -->
          <div class="drop-shadow-xs drop-shadow-color-black">&amp; More</div>
          <div class="mt-8">
            <AButton
              :to="`/industries/${industry.slug}`"
              label="Learn More"
              size="xlarge"
              material="empty"
            />
          </div>
          <div v-if="index < industries.length - 1">
            <AButton
              size="xlarge"
              material="empty"
              label-class="text-left whitespace-normal"
              class="w-fit"
              @click="toAnchor(index, industries[index + 1]?.slug)"
            >
              <span
                class="hyphens-manual sm:hyphens-none"
                v-html="`Next: ${industries[index + 1]?.display_name || industries[index + 1]?.name}`"
              />
            </AButton>
          </div>
          <div v-if="index !== 0">
            <AButton
              label="Back to top"
              size="large"
              material="empty"
              class="opacity-80"
              @click="toAnchor(index, 'top')"
            />
          </div>
        </ul>
        <div class="overlay-el bg-icsblue/63 opacity-0 absolute-inset mix-blend-multiply -z-1" />
        <div class="overlay-el opacity-0 absolute-inset from-white/62 to-white/38 bg-linear-to-b -z-2" />
        <div class="overlay-el bg-icsblue opacity-0 absolute-inset mix-blend-overlay -z-2" />
        <video
          autoplay
          playsinline
          muted
          class="absolute-inset object-cover size-full -z-3"
          :poster="industry.video_poster"
        >
          <source
            v-if="industry.video_webm"
            :src="`${config.public.directusUrl}/assets/${industry.video_webm}`"
            type="video/webm"
          >
          <source
            :src="`${config.public.directusUrl}/assets/${industry.video_mp4}`"
            type="video/mp4"
          >
        </video>
      </div>
    </BlockWindow>
    <NuxtPage v-else />
  </PageRoot>
</template>
